import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'

import { PageTitle } from '../../../../../_metronic/layout/core'
import { KTIcon } from '../../../../../_metronic/helpers'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { FilterManager } from '../../../../common/filter/FilterManager'
import { ISelectOption } from '../../../../common/types/select'

import {
  deleteProfile,
  getBankLookup,
  getMerchantLookup,
  getPaymentMethodLookup,
  getSchema,
  listProfiles,
  setProfileActive,
} from './core/api'
import { MATCHING_PROFILES_BREADCRUMBS } from './core/consts'
import { readApiError } from './core/errors'
import { IProfileFilters, MatchingProfile, ProfileListParams, ProfileSchema } from './core/types'
import { ProfileFormModal } from './components/ProfileFormModal'
import { CollapsibleCell } from './components/CollapsibleCell'
import { escapeHtml } from '../../../../common/escapeHtml'

const emptyFilters: IProfileFilters = {}

// Low-saturation, theme-aware chip palettes. Conditions read as a soft amber
// group; values as a neutral slate group — both calmer than the stock
// badge-light-* and clearly distinct from the direction badges.
// Bases are :root-level vars (gray-*, warning, primary) — never --bs-card-bg,
// which is undefined outside .card and would make the chip transparent when it
// renders inside the portaled expand panel.
const CONDITION_CHIP: React.CSSProperties = {
  backgroundColor: 'color-mix(in srgb, var(--bs-warning) 22%, var(--bs-gray-100))',
  color: 'color-mix(in srgb, var(--bs-warning) 80%, var(--bs-gray-900))',
  border: '1px solid color-mix(in srgb, var(--bs-warning) 35%, transparent)',
  fontWeight: 500,
}
const VALUE_CHIP: React.CSSProperties = {
  backgroundColor: 'color-mix(in srgb, var(--bs-primary) 9%, var(--bs-gray-200))',
  color: 'var(--bs-gray-800)',
  border: '1px solid color-mix(in srgb, var(--bs-primary) 18%, var(--bs-gray-300))',
  fontWeight: 500,
}

// Renders a profile's applicability conditions (matching_profile.filters) as
// readable chips instead of a raw JSON string, so the list shows at a glance what
// each profile targets. All condition chips share one colour (warning) so the
// column reads as a single semantic group, distinct from direction and values.
const FilterChips: React.FC<{
  filters: Record<string, unknown>
  // code → человекочитаемое имя банка; пусто, пока справочник не загрузился —
  // тогда показываем сам код, а не пустой чип.
  bankName?: Record<string, string>
  methodName?: Record<number, string>
}> = ({ filters, bankName = {}, methodName = {} }) => {
  const chips: React.ReactNode[] = []
  const min = filters.amount_min as string | undefined
  const max = filters.amount_max as string | undefined
  if (min || max) {
    const label = min && max ? `${min}–${max}` : min ? `от ${min}` : `до ${max}`
    chips.push(
      <span key='amount' className='badge me-1 mb-1' style={CONDITION_CHIP}>
        сумма: {label}
      </span>
    )
  }
  if (filters.currency) {
    chips.push(
      <span key='currency' className='badge me-1 mb-1' style={CONDITION_CHIP}>
        валюта: {String(filters.currency)}
      </span>
    )
  }
  // Банк — такое же условие применимости, как сумма и валюта (CPAYMENT-1748):
  // чип на банк, чтобы длинный список переносился и сворачивался, а не резался.
  if (Array.isArray(filters.banks)) {
    ;(filters.banks as unknown[]).forEach((code) => {
      const key = String(code)
      chips.push(
        <span key={`bank-${key}`} className='badge me-1 mb-1' style={CONDITION_CHIP}>
          банк: {bankName[key] ?? key}
        </span>
      )
    })
  }
  // Платёжный метод — условие применимости на обоих направлениях (CPAYMENT-1695):
  // на PAYOUT сравнивается метод выплаты, на PAYMENT — метод депозита.
  if (Array.isArray(filters.payment_method)) {
    ;(filters.payment_method as unknown[]).forEach((id) => {
      const key = Number(id)
      chips.push(
        <span key={`method-${key}`} className='badge me-1 mb-1' style={CONDITION_CHIP}>
          метод: {methodName[key] ?? key}
        </span>
      )
    })
  }
  if (!chips.length) return <span className='text-muted'>—</span>
  return <>{chips}</>
}

// Formats a config value for a compact label: arrays join with commas, scalars
// stringify as-is.
const formatConfigValue = (v: unknown): string => {
  if (Array.isArray(v)) return v.join(', ')
  if (v === null || v === undefined) return ''
  return String(v)
}

// Renders a profile's config overrides (matching_profile.config) as
// `PARAM: value` chips in one neutral slate colour — its own visual group,
// distinct from conditions and direction.
const ConfigChips: React.FC<{ config: Record<string, unknown> }> = ({ config }) => {
  const entries = Object.entries(config ?? {}).filter(([, v]) => {
    if (v === null || v === undefined || v === '') return false
    if (Array.isArray(v) && v.length === 0) return false
    return true
  })
  if (!entries.length) return <span className='text-muted'>—</span>
  return (
    <>
      {entries.map(([k, v]) => (
        <span key={k} className='badge me-1 mb-1' style={VALUE_CHIP}>
          {k}: {formatConfigValue(v)}
        </span>
      ))}
    </>
  )
}

export const MatchingProfilesPage: React.FC = () => {
  const [schema, setSchema] = useState<ProfileSchema | null>(null)
  const [merchantOptions, setMerchantOptions] = useState<ISelectOption<number>[]>([])
  const [bankOptions, setBankOptions] = useState<ISelectOption<string>[]>([])
  const [methodOptions, setMethodOptions] = useState<ISelectOption<number>[]>([])
  const [items, setItems] = useState<MatchingProfile[]>([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState<MatchingProfile | null>(null)
  const [currentParams, setCurrentParams] = useState<ProfileListParams>({})
  const [togglingId, setTogglingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  // Schema + merchant options are fetched ONCE on entering the section. Both are
  // reported on failure: this URL is carved out of the interceptor's generic toast,
  // so an unhandled rejection here leaves the operator with a form that silently
  // has no fields (CPAYMENT-1723).
  useEffect(() => {
    getSchema()
      .then((r) => r.data.success && setSchema(r.data.data))
      .catch((e) => Swal.fire({ title: 'Ошибка', text: readApiError(e, 'Не удалось загрузить схему полей').message, icon: 'error' }))
    getMerchantLookup()
      .then((r) => r.data.success && setMerchantOptions(r.data.data ?? []))
      .catch((e) => Swal.fire({ title: 'Ошибка', text: readApiError(e, 'Не удалось загрузить список мерчантов').message, icon: 'error' }))
    getBankLookup()
      .then((r) => r.data.success && setBankOptions(r.data.data ?? []))
      .catch((e) => Swal.fire({ title: 'Ошибка', text: readApiError(e, 'Не удалось загрузить список банков').message, icon: 'error' }))
    getPaymentMethodLookup()
      .then((r) => r.data.success && setMethodOptions(r.data.data ?? []))
      .catch((e) =>
        Swal.fire({ title: 'Ошибка', text: readApiError(e, 'Не удалось загрузить список платёжных методов').message, icon: 'error' })
      )
  }, [])

  const merchantName = useMemo(() => {
    const m: Record<number, string> = {}
    merchantOptions.forEach((o) => {
      m[o.value] = o.label
    })
    return m
  }, [merchantOptions])

  // Тот же справочник, что питает мультиселект в форме: в профиле лежит код банка,
  // а в списке показываем название.
  // Тот же справочник, что питает мультиселект методов в форме: в профиле лежит id,
  // а в списке показываем тип метода.
  const methodName = useMemo(() => {
    const m: Record<number, string> = {}
    methodOptions.forEach((o) => {
      m[o.value] = o.label
    })
    return m
  }, [methodOptions])

  const bankName = useMemo(() => {
    const m: Record<string, string> = {}
    bankOptions.forEach((o) => {
      m[o.value] = o.label
    })
    return m
  }, [bankOptions])

  const loadData = useCallback(async (params: ProfileListParams) => {
    setLoading(true)
    try {
      const r = await listProfiles(params)
      if (r.data.success) setItems(r.data.data ?? [])
    } catch (e) {
      Swal.fire({ title: 'Ошибка', text: readApiError(e, 'Не удалось загрузить профили').message, icon: 'error' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData(currentParams)
  }, [loadData, currentParams])

  const handleApplyFilters = useCallback((f: IProfileFilters) => {
    const params: ProfileListParams = {}
    if (f.merchant) params.merchant_id = Number(f.merchant)
    if (f.direction) params.direction = f.direction
    if (f.is_active) params.is_active = f.is_active === 'true'
    setCurrentParams(params)
  }, [])

  const merchantSelectOptions = useMemo(
    () => merchantOptions.map((o) => ({ value: String(o.value), label: o.label })),
    [merchantOptions]
  )
  const directionOptions = useMemo(
    () => (schema?.directions ?? ['PAYMENT', 'PAYOUT']).map((d) => ({ value: d, label: d })),
    [schema]
  )
  const activeOptions = [
    { value: 'true', label: 'Активные' },
    { value: 'false', label: 'Неактивные' },
  ]

  const openCreate = () => {
    setEditing(null)
    setShowModal(true)
  }
  const openEdit = (p: MatchingProfile) => {
    setEditing(p)
    setShowModal(true)
  }
  // Hard delete: the confirm names the profile and says the row is gone for good,
  // because the neighbouring switch is the reversible action and the two must not
  // read alike.
  const removeProfile = async (p: MatchingProfile) => {
    const confirmed = await Swal.fire({
      title: 'Удалить профиль?',
      html: `<b>${escapeHtml(p.name || p.code)}</b><br/>Профиль будет удалён без возможности восстановить.<br/>Чтобы просто отключить его, используйте переключатель «Активен».`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Удалить',
      cancelButtonText: 'Отмена',
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-light' },
      buttonsStyling: false,
    })
    if (!confirmed.isConfirmed) return
    setDeletingId(p.id)
    try {
      const r = await deleteProfile(p.id)
      if (r.data.success) await loadData(currentParams)
      else Swal.fire({ title: 'Ошибка', text: 'Не удалось удалить профиль', icon: 'error' })
    } catch (e) {
      Swal.fire({ title: 'Ошибка', text: readApiError(e, 'Не удалось удалить профиль').message, icon: 'error' })
    } finally {
      setDeletingId(null)
    }
  }

  const toggleActive = async (p: MatchingProfile) => {
    setTogglingId(p.id)
    try {
      await setProfileActive(p.id, !p.is_active)
      await loadData(currentParams)
    } catch (e) {
      // Without this the switch just snapped back with no explanation — the
      // interceptor's generic toast is disabled for this URL.
      Swal.fire({ title: 'Ошибка', text: readApiError(e, 'Не удалось изменить статус профиля').message, icon: 'error' })
    } finally {
      setTogglingId(null)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={MATCHING_PROFILES_BREADCRUMBS as any}>Профили мэтчинга</PageTitle>

      <FilterManager<IProfileFilters>
        activeFilters={[
          { filter: 'select', fieldName: 'merchant' },
          { filter: 'select', fieldName: 'direction' },
          { filter: 'select', fieldName: 'is_active' },
        ]}
        defaultValues={emptyFilters}
        onApplyFilters={handleApplyFilters}
        filterProps={{
          merchant: { label: 'Мерчант', options: merchantSelectOptions, placeholder: 'Все мерчанты' },
          direction: { label: 'Направление', options: directionOptions, placeholder: 'Все направления' },
          is_active: { label: 'Статус', options: activeOptions, placeholder: 'Все статусы' },
        }}
      />

      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h3 className='card-title'>Профили мэтчинга</h3>
          <button type='button' className='btn btn-sm btn-primary' onClick={openCreate} disabled={!schema}>
            <KTIcon iconName='plus' className='fs-4 me-1' />
            Добавить
          </button>
        </div>
        <div className='card-body'>
          {loading ? (
            <div className='text-muted'>Загрузка…</div>
          ) : items.length === 0 ? (
            <div className='text-muted'>Записей нет</div>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  <th>Название</th>
                  <th>Мерчант</th>
                  <th>Направление</th>
                  <th>Условия</th>
                  <th>Значения</th>
                  <th>Приоритет</th>
                  <th className='text-center'>Активен</th>
                  <th />
                </tr>
              </TableHead>
              <TableBody>
                {items.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <div className='fw-semibold text-gray-900'>{p.name}</div>
                      <div className='text-gray-500 fs-8 text-monospace'>{p.code}</div>
                    </td>
                    <td>{merchantName[p.merchant_id] ?? p.merchant_id}</td>
                    <td>
                      <span className={`badge badge-light-${p.direction === 'PAYOUT' ? 'primary' : 'info'}`}>
                        {p.direction}
                      </span>
                    </td>
                    <td style={{ maxWidth: 360, minWidth: 240 }}>
                      {/* Тот же сворачивающийся контейнер, что у «установленных параметров»:
                          список банков длиннее двух строк не растягивает строку таблицы, а
                          раскрывается подложкой при наведении. */}
                      <CollapsibleCell mode='hover'>
                        <FilterChips filters={p.filters ?? {}} bankName={bankName} methodName={methodName} />
                      </CollapsibleCell>
                    </td>
                    <td style={{ maxWidth: 360, minWidth: 240 }}>
                      <CollapsibleCell mode='hover'>
                        <ConfigChips config={p.config ?? {}} />
                      </CollapsibleCell>
                    </td>
                    <td>{p.priority}</td>
                    <td className='text-center'>
                      <button
                        type='button'
                        className={`btn btn-sm ${p.is_active ? 'btn-light-success' : 'btn-light-danger'} min-w-100px px-2 py-1`}
                        disabled={togglingId === p.id}
                        onClick={() => toggleActive(p)}
                      >
                        {togglingId === p.id ? (
                          <span className='spinner-border spinner-border-sm' />
                        ) : p.is_active ? (
                          'Активен'
                        ) : (
                          'Выключен'
                        )}
                      </button>
                    </td>
                    <td className='text-end'>
                      <button
                        type='button'
                        className='btn btn-icon btn-light btn-sm'
                        title='Изменить'
                        onClick={() => openEdit(p)}
                      >
                        <KTIcon iconName='pencil' className='fs-3' />
                      </button>
                      <button
                        type='button'
                        className='btn btn-icon btn-light-danger btn-sm ms-2'
                        title='Удалить'
                        disabled={deletingId === p.id}
                        onClick={() => removeProfile(p)}
                      >
                        {deletingId === p.id ? (
                          <span className='spinner-border spinner-border-sm' />
                        ) : (
                          <KTIcon iconName='trash' className='fs-3' />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {schema && (
        <ProfileFormModal
          show={showModal}
          onClose={() => setShowModal(false)}
          editing={editing}
          schema={schema}
          merchantOptions={merchantOptions}
          bankOptions={bankOptions}
          methodOptions={methodOptions}
          onSaved={() => loadData(currentParams)}
        />
      )}
    </>
  )
}

export default MatchingProfilesPage
